import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/useAuth";
import { Button } from "../components/ui/Button";
import { CheckIcon } from "../components/ui/icons";
import { s } from "./AwaitingAssociationPage.styles";

/** Poll cadence for the silent association check. */
const POLL_INTERVAL_MS = 30_000;
/** Tick cadence for the "last refresh was Ns ago" label so the relative
 *  time updates without waiting on the next poll. */
const CLOCK_TICK_MS = 1_000;

/**
 * Shown to authenticated users whose `associations[]` is empty — typically
 * accounts that just self-signed-up and are waiting on an admin to assign
 * them to a customer/operation. No new route; rendered as a layout-level
 * gate by `DashboardLayout`.
 */
export default function AwaitingAssociationPage() {
  const { t } = useTranslation();
  const { user, isNewUser, logout, refreshSession } = useAuth();

  // Seed with the moment the page mounts — the login that landed the user
  // here counts as the first "check", so we never show an empty state.
  const [lastRefreshAt, setLastRefreshAt] = useState<number>(() => Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const refreshingRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      setRefreshing(true);
      try {
        await refreshSession();
        if (cancelled) return;
        // applyLoginResponse already wrote new associations into the
        // AuthProvider; the layout-level gate will route us out on the
        // next render if associations.length > 0.
        setLastRefreshAt(Date.now());
      } catch {
        // Network blip or backend hiccup — the next tick retries.
      } finally {
        refreshingRef.current = false;
        if (!cancelled) setRefreshing(false);
      }
    }
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshSession]);

  // Keep "Ns ago" alive between polls.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  function formatElapsed(at: number): string {
    const secs = Math.max(0, Math.floor((now - at) / 1000));
    if (secs < 5) return t("common:time.justNow");
    if (secs < 60) return t("common:time.secondsAgo", { count: secs });
    const mins = Math.floor(secs / 60);
    return t("common:time.minutesAgo", { count: mins });
  }

  const lastRefreshLabel = refreshing
    ? t("auth:awaitingAssociation.checking")
    : t("auth:awaitingAssociation.lastRefresh", { when: formatElapsed(lastRefreshAt) });

  return (
    <div className={s.page}>
      <div className={s.card}>
        <div className={s.iconWrap}>
          <CheckIcon className={s.icon} />
        </div>
        <h1 className={s.title}>
          {t("auth:awaitingAssociation.title")}
        </h1>
        <p className={s.subtitle}>
          {t("auth:awaitingAssociation.subtitle")}
        </p>
        <p className={s.body}>
          {t("auth:awaitingAssociation.body")}
        </p>
        {user && (
          <div className={s.userBlock}>
            <div>{user.email}</div>
            <div>{user.name}</div>
            {isNewUser !== undefined && (
              <span className={isNewUser ? s.newAccountTag : s.existingAccountTag}>
                {isNewUser
                  ? t("auth:awaitingAssociation.createdNow")
                  : t("auth:awaitingAssociation.alreadyExisted")}
              </span>
            )}
          </div>
        )}
        <div className={s.pollStatus}>
          <span className={s.pollDot} />
          {lastRefreshLabel}
        </div>
        <Button variant="secondary" onClick={logout}>
          {t("common:actions.back")}
        </Button>
      </div>
    </div>
  );
}
